import { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'urql';
import { Period, SocialAdsAnalytics_QueryFragment } from '@frontend/gql/social/graphql';
import { getTopAdsSumInsights } from '../graphql';
import { TopAdData } from '../components/ads-table/ads-table-types';
import { filterAndSortAdsData } from './use-social-ads-data-utils';

/**
 * Sanitizes an array of IDs by trimming whitespace, filtering out empty strings,
 * and removing duplicates. Returns undefined if the resulting array is empty.
 */
const sanitizeIds = (ids?: string[]): string[] | undefined => {
  if (!ids || ids.length === 0) {
    return undefined;
  }

  const cleanIds = [...new Set(ids.map((id) => id.trim()).filter((id) => id.length > 0))];
  return cleanIds.length > 0 ? cleanIds : undefined;
};

export interface UseSocialAdsTopAdsDataProps {
  clientId?: string;
  currentIntervalRange: {
    startDate: string;
    endDate: string;
    precision: Period;
  };
  previousIntervalRange: {
    startDate: string;
    endDate: string;
  };
  startDate: string;
  endDate: string;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignAndSetIds?: string[];
  adObjectiveIds?: string[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
  tab: string;
}

export interface UseSocialAdsTopAdsDataResult {
  fetching: boolean;
  refetching: boolean;
  processedData: TopAdData[]; // Always an array, even if empty
  handleRefetch: () => void;
}

export const useSocialAdsTopAdsData = ({
  clientId,
  currentIntervalRange,
  // previousIntervalRange,
  // startDate,
  // endDate,
  // fromOldData,
  adAccountIds,
  // adCampaignAndSetIds,
  // adObjectiveIds,
  // memberIds,
  // compareWithPartnershipAds,
  tab,
}: UseSocialAdsTopAdsDataProps): UseSocialAdsTopAdsDataResult => {
  const [refetching, setRefetching] = useState(false);

  // Sanitize adAccountIds to remove empty strings, whitespace, and duplicates
  const cleanAdAccountIds = useMemo(() => sanitizeIds(adAccountIds), [adAccountIds]);

  const [result, reexecuteQuery] = useQuery({
    query: getTopAdsSumInsights,
    variables: {
      clientId: clientId || '',
      currentIntervalRange: {
        startDate: currentIntervalRange.startDate,
        endDate: currentIntervalRange.endDate,
        precision: currentIntervalRange.precision,
      },
      ...(cleanAdAccountIds && { adAccountIds: cleanAdAccountIds }),
    },
    pause: !clientId || tab !== 'top-ads',
  });

  const { data, fetching } = result;

  // Use data?.client directly instead of useFragment
  const client = (data as SocialAdsAnalytics_QueryFragment)?.client;

  // Use shared filtering and sorting function to ensure consistency with useAdsQueryData
  const topAds: TopAdData[] = useMemo(() => {
    const adsData = client?.sumInsightsBy || [];
    const sortedAds = filterAndSortAdsData(adsData);
    return sortedAds; // Return full dataset for client-side pagination
  }, [client]);

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    reexecuteQuery({ requestPolicy: 'network-only' });
    setTimeout(() => setRefetching(false), 500);
  }, [reexecuteQuery]);

  return {
    fetching,
    refetching,
    processedData: topAds,
    handleRefetch,
  };
};
